import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { withLightTheme } from '../../utils/captureLight'
import { getPaperSize } from '../../utils/printPaper'

const MARGIN_MM = 10
const FOOTER_MM = 6

// VH-228/223: client PDF of exactly the report on screen (one version or one week),
// with the stamp (version and finalization date, or week dates) on every page.
// html2canvas-pro, not html2canvas: Tailwind 4 oklch() colours break the original.
export function CycleReportPdfButton({ targetRef, fileName, stamp }) {
  const { t, i18n } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const exportPdf = async () => {
    if (!targetRef?.current) return
    setBusy(true)
    setFailed(false)
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas-pro'),
        import('jspdf'),
      ])
      const element = targetRef.current
      const canvas = await withLightTheme(element, () =>
        html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' }))
      const pdf = new jsPDF('p', 'mm', getPaperSize(i18n.language))
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const usableWidth = pageWidth - MARGIN_MM * 2
      const usableHeight = pageHeight - MARGIN_MM * 2 - FOOTER_MM
      const ratio = usableWidth / canvas.width
      const sliceHeightPx = Math.max(1, Math.floor(usableHeight / ratio))
      let cursor = 0
      let page = 0
      while (cursor < canvas.height) {
        if (page > 0) pdf.addPage()
        const height = Math.min(sliceHeightPx, canvas.height - cursor)
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = canvas.width
        pageCanvas.height = height
        const ctx = pageCanvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, height)
        ctx.drawImage(canvas, 0, cursor, canvas.width, height, 0, 0, canvas.width, height)
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN_MM, MARGIN_MM, usableWidth, height * ratio)
        pdf.setFontSize(8)
        pdf.text(`${stamp} · ${page + 1}`, MARGIN_MM, pageHeight - MARGIN_MM / 2)
        cursor += height
        page += 1
      }
      pdf.save(fileName)
    } catch (err) {
      console.error('Cycle report PDF export failed', err)
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" className="btn-outline text-sm inline-flex items-center gap-1" disabled={busy} onClick={exportPdf}>
        <ArrowDownTrayIcon className="h-4 w-4" />
        {busy ? t('cycleReports.pdf.exporting') : t('cycleReports.pdf.export')}
      </button>
      {failed && <span className="text-xs text-red-600" role="alert">{t('cycleReports.pdf.failed')}</span>}
    </span>
  )
}

export default CycleReportPdfButton
